/**
 * client/net/session.ts — ClientSession: handshake/lobby control flow over a Transport, plus the
 * per-frame network pipeline that produces the ClientWorld [NET §7, ARCHITECTURE §4]:
 *   snapshots -> clock (Q3 CL_AdjustTimeDelta) -> interpolation of remote entities;
 *   input -> 60 Hz cmds -> send + predict (shared pmove, clip against INTERPOLATED players);
 *   own projectiles predicted; events scheduled on the render timeline; reconnect.
 *
 * OWNER: NETCODE agent.
 *
 * Reconnect [NET §10]: `welcome.token` is stored in sessionStorage with the room code. When the
 * socket drops mid-session the session retries every RECONNECT_RETRY_MS for RECONNECT_GRACE_MS with
 * `hello{room, token}`; a page reload that joins the same room (?room=CODE) also sends the token,
 * so a refreshed tab gets its slot (score, ammo, position) back.
 * Deviation (README promise "reloads the tab"): NET §10 keeps the token in sessionStorage only, so a
 * friend who CLOSED the tab and clicked the link again joined as a new player (0 score) next to a
 * 30 s ghost. The token is now also kept in localStorage; a new tab uses it only when no live tab
 * holds it (Web Locks: the owning tab holds `bo2sns.slot.<token>` until it closes), so two tabs
 * opened on purpose in one browser still get two players.
 *
 * Stall detection: Q3/IW CG_DrawDisconnect shows "Connection Interrupted" on the client when its
 * commands stop being acknowledged (oldest of CMD_BACKUP cmds unacked, ~1 s); here: no snapshot for
 * CONNECTION_INTERRUPTED_MS while playing -> the local predicted state carries
 * EF.CONNECTION_INTERRUPTED, which the HUD draws. A socket that delivers nothing at all (the server
 * pings every 1 s) for STALL_RECONNECT_MS is reopened by the client itself instead of waiting for
 * the browser's TCP close (minutes on a dead path); the server lets the token take the slot over.
 * Password: `?password=` (or `?pw=`) is passed in `hello` for servers started with SERVER_PASSWORD.
 */
import {
  CMD_REDUNDANCY_UNRELIABLE, CONNECTION_INTERRUPTED_MS, PITCH_DOWN_LIMIT, PITCH_UP_LIMIT, PROTOCOL_VERSION, RECONNECT_GRACE_MS,
} from '@shared/constants';
import type { CollisionWorld, ClipEntity } from '@shared/collision/types';
import { createEmptyWorld } from '@shared/collision/boxworld';
import { shortToAngle, shortToSigned, vclone, vlerp, type Vec3 } from '@shared/math';
import { createPmoveContext } from '@shared/pmove/context';
import { decodeSnapshot, encodeCmdPacket, peekMsgType, type PosQuant } from '@shared/protocol/codec';
import type { ClientControl, MapName, RoomSettings, RoomState, ServerControl } from '@shared/protocol/control';
import { createClipEntity, setPlayerClipEntity } from '@shared/protocol/entities';
import { MsgType } from '@shared/protocol/ids';
import type { Transport, WireData } from '@shared/protocol/transport';
import { copyPlayerState, createPlayerState } from '@shared/playerstate';
import {
  EF, GameEventType, MatchPhase, MoveFlag, type PlayerInfo, type ScoreRow, type Snapshot, type UserCmd,
} from '@shared/types';
import type {
  ClientWorld, CmdIntent, FrameEvent, InputContext, InputFrame, InputSource, LocalView, NetStats, RenderMissile, RenderPlayer,
} from '../types';
import { ServerClock } from './clock';
import { CmdGenerator } from './cmdgen';
import { EventScheduler } from './events';
import { SnapshotBuffer, interpolateMissiles, interpolatePlayers, resolveMissile, type ParentPose } from './interp';
import { Predictor, type CorrectionInfo, type PredictedEvent } from './prediction';
import { ProjectilePredictor } from './projectiles';
import { createPose, type PlayerPose } from '@sim/combat/hitvolumes';
import { isReopenable, type ReopenableTransport } from './transport';

const NEUTRAL_INTENT: CmdIntent = { buttons: 0, forwardmove: 0, rightmove: 0, weapon: 0, meleeChargeYaw: 0, meleeChargeDist: 0 };

export type SessionState = 'connecting' | 'lobby' | 'loading' | 'playing' | 'ended' | 'reconnecting' | 'closed';

/** NET §10: retry every 1 s for 30 s. */
export const RECONNECT_RETRY_MS = 1000;
/**
 * ESTIMATE: reopen the socket after this long without ANY server message (pings come every 1 s,
 * PING_INTERVAL_MS). Long enough for a GC/loading hitch, short against the 30 s reconnect grace.
 */
export const STALL_RECONNECT_MS = 5000;
const WATCHDOG_MS = 500;
const STORAGE_KEY = 'bo2sns.reconnect';
const SHARED_KEY = 'bo2sns.reconnect.shared';
const LOCK_PREFIX = 'bo2sns.slot.';

export interface LoadInfo {
  map: MapName;
  cmdTimeBase: number;
  quant: PosQuant;
}

class ClientWorldImpl implements ClientWorld {
  connected = false;
  localClientNum = -1;
  map: MapName | null = null;
  renderTime = 0;
  predicted = createPlayerState();
  view: LocalView = { origin: [0, 0, 0], viewangles: [0, 0, 0], viewHeight: 60, velocity: [0, 0, 0], alpha: 0 };
  players: RenderPlayer[] = [];
  missiles: RenderMissile[] = [];
  playerInfo = new Map<number, PlayerInfo>();
  scoreboard: ScoreRow[] = [];
  phase: MatchPhase = MatchPhase.WAITING;
  timeLeftMs = 0;
  timeLimitMs = 0;
  stats: NetStats = {
    ping: 0, delta: 0, renderLag: 0, extrapolationsPerSec: 0, correctionsPerSec: 0, maxPredError: 0, snapBytes: 0,
    cmdsPerPacket: 1, serverTick: 0, transport: '',
  };
  frameEvents: FrameEvent[] = [];
  getPlayer(clientNum: number): RenderPlayer | undefined {
    for (const p of this.players) if (p.clientNum === clientNum) return p;
    return undefined;
  }
}

/** Extra counters for tests / the console (window.__game.session.netDebug). */
export interface NetDebug {
  snapshots: number;
  snapshotsDropped: number;
  cmdsSent: number;
  cmdSlotsDropped: number;
  pendingCmds: number;
  corrections: number;
  lastPredError: number;
  clockResets: number;
  eventsDropped: number;
  projectilesLaunched: number;
  projectilesConfirmed: number;
  projectilesExpired: number;
  reconnects: number;
  decodeErrors: number;
  /** Times the snapshot stream stopped for CONNECTION_INTERRUPTED_MS while playing. */
  stalls: number;
  /** Times the watchdog reopened a silent socket. */
  watchdogReconnects: number;
  /** [dropped serverTime, newest serverTime] of the last out-of-order snapshot. */
  lastSnapshotDrop: [number, number] | null;
  /** Last prediction corrections with the first differing PlayerState field (desync debugging). */
  lastCorrections: readonly CorrectionInfo[];
}

interface StoredSlot { room: string; token: string }

function storage(kind: 'session' | 'local'): Storage | null {
  try {
    if (kind === 'session') return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch { return null; }
}
function readStore(kind: 'session' | 'local', key: string): StoredSlot | null {
  try {
    const s = storage(kind)?.getItem(key);
    const v = s ? (JSON.parse(s) as StoredSlot) : null;
    return v && typeof v.room === 'string' && typeof v.token === 'string' ? v : null;
  } catch { return null; }
}
function writeStore(kind: 'session' | 'local', key: string, v: StoredSlot | null): void {
  try { const st = storage(kind); if (!st) return; if (v) st.setItem(key, JSON.stringify(v)); else st.removeItem(key); } catch { /* private mode */ }
}
function readSlot(): StoredSlot | null { return readStore('session', STORAGE_KEY); }
function writeSlot(v: StoredSlot | null): void {
  writeStore('session', STORAGE_KEY, v);
  if (v) writeStore('local', SHARED_KEY, v);
}
/** Forget the shared (cross-tab) token, but only if it is ours. */
function clearSharedSlot(token: string): void {
  if (token && readStore('local', SHARED_KEY)?.token === token) writeStore('local', SHARED_KEY, null);
}
interface LockManagerLike {
  request(name: string, opts: { ifAvailable?: boolean }, cb: (lock: unknown) => Promise<void>): Promise<void>;
  query(): Promise<{ held?: { name?: string }[] }>;
}
function lockManager(): LockManagerLike | null {
  try {
    const n = typeof navigator !== 'undefined' ? (navigator as unknown as { locks?: LockManagerLike }).locks : undefined;
    return n ?? null;
  } catch { return null; }
}
/**
 * Token to send in `hello` for `room`: this tab's own (reload) at once; else the browser-wide one
 * (tab closed and link re-opened) when no live tab holds its lock. Without Web Locks the shared
 * token is used (the server lets it take over a stale socket).
 */
export async function pickReconnectToken(room: string): Promise<string | undefined> {
  const own = readSlot();
  if (own && own.room === room) return own.token;
  const shared = readStore('local', SHARED_KEY);
  if (!shared || shared.room !== room) return undefined;
  const locks = lockManager();
  if (!locks) return shared.token;
  try {
    const q = await locks.query();
    if ((q.held ?? []).some((l) => l.name === LOCK_PREFIX + shared.token)) return undefined; // a live tab plays this slot
  } catch { /* fall through */ }
  return shared.token;
}
function urlPassword(): string | undefined {
  try {
    const q = new URLSearchParams(location.search);
    return q.get('password') ?? q.get('pw') ?? undefined;
  } catch { return undefined; }
}

export class ClientSession {
  readonly world = new ClientWorldImpl();
  state: SessionState = 'connecting';
  room: RoomState | null = null;
  load: LoadInfo | null = null;
  lastError: string | null = null;
  readonly netDebug: NetDebug = {
    snapshots: 0, snapshotsDropped: 0, cmdsSent: 0, cmdSlotsDropped: 0, pendingCmds: 0, corrections: 0, lastPredError: 0,
    clockResets: 0, eventsDropped: 0, projectilesLaunched: 0, projectilesConfirmed: 0, projectilesExpired: 0, reconnects: 0, decodeErrors: 0,
    stalls: 0, watchdogReconnects: 0,
    lastCorrections: [],
    lastSnapshotDrop: null,
  };

  private clock = new ServerClock();
  private readonly snaps = new SnapshotBuffer();
  private readonly cmdgen = new CmdGenerator();
  private readonly events = new EventScheduler();
  private predictor!: Predictor;
  private projectiles!: ProjectilePredictor;
  private collision: CollisionWorld = createEmptyWorld();
  /** Remote players' clip boxes at their interpolated positions (Q3 CG_ClipMoveToEntities). */
  private readonly clipEnts: ClipEntity[] = [];
  private readonly clipPool: ClipEntity[] = [];
  /** Remote players' interpolated hit-volume poses, for own-projectile prediction (projectiles.ts). */
  private readonly targetPoses: PlayerPose[] = [];
  private readonly posePool: PlayerPose[] = [];
  private listeners: ((m: ServerControl) => void)[] = [];
  private readonly predEvents: PredictedEvent[] = [];
  private readonly sentCmds: UserCmd[] = [];
  private statWindowStart = 0;
  private statCorrections = 0;
  private statExtrap = 0;
  private lastFrameReal = 0;
  private token = '';
  private roomCode = '';
  private leaving = false;
  private reconnectUntil = 0;
  private reconnectTimer: ReturnType<typeof setInterval> | null = null;
  private readonly tmpErr: Vec3 = [0, 0, 0];
  /** performance.now() of the last message of any kind / the last snapshot. */
  private lastMsgAt = 0;
  private lastSnapAt = 0;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  private lastWatchdog = 0;
  /** hello() is waiting for the cross-tab token check: other sends queue behind it. */
  private helloPending = false;
  private readonly preHello: string[] = [];
  private releaseLock: (() => void) | null = null;
  private lockedToken = '';
  /** Display copy of the predicted state while stalled (carries EF.CONNECTION_INTERRUPTED). */
  private readonly stalledPs = createPlayerState();
  /** True while playing and no snapshot arrived for CONNECTION_INTERRUPTED_MS (Q3 CG_DrawDisconnect). */
  interrupted = false;
  /**
   * CONTRACT-REQUESTS #input-1.4: while a menu is open the app sets this; cmds keep flowing (so the
   * server does not flag CONNECTION_INTERRUPTED) but carry neutral intents and no look input.
   */
  inputSuspended = false;
  /** CONTRACT-REQUESTS #input-1.3: this frame's InputFrame (pause / scoreboardHeld for app + hud). */
  lastInputFrame: InputFrame | null = null;
  /**
   * CONTRACT-REQUESTS #V1: view kick (deg, [pitch, yaw, roll]) added to the angles of the cmds built
   * this frame, NOT to the persistent client view angles (T5 CL_SetUserCmdAimValues + cg.kickAngles).
   * The camera writes it (previous frame's kick); zero = no effect.
   */
  readonly aimKickAngles: Vec3 = [0, 0, 0];

  constructor(private readonly transport: Transport, private readonly name: string, transportLabel: string) {
    this.world.stats.transport = transportLabel;
    this.world.stats.cmdsPerPacket = transport.reliable ? 1 : 1 + CMD_REDUNDANCY_UNRELIABLE;
    this.rebuildPrediction();
    transport.onMessage((d) => this.onMessage(d));
    transport.onClose((r) => this.onTransportClose(r));
    if (isReopenable(transport)) {
      transport.onOpen(() => this.onTransportOpen());
      this.watchdogTimer = setInterval(() => this.watchdog(), WATCHDOG_MS);
    }
  }

  private rebuildPrediction(): void {
    const ctx = createPmoveContext({ world: this.collision, getEntities: () => this.clipEnts });
    this.predictor = new Predictor(ctx);
    this.projectiles = new ProjectilePredictor(this.collision, () => this.targetPoses);
    this.predictor.onFreshEvent = (ps, e) => this.projectiles.onPredictedEvent(ps, e.ev, e.parm, performance.now());
  }

  /** Use the map's collision world for prediction (call after loading, before `loaded`). */
  setCollisionWorld(w: CollisionWorld): void {
    this.collision = w;
    this.rebuildPrediction();
  }

  onControl(cb: (m: ServerControl) => void): void {
    this.listeners.push(cb);
  }

  send(msg: ClientControl): void {
    const s = JSON.stringify(msg);
    if (this.helloPending) { this.preHello.push(s); return; }
    this.transport.send(s);
  }

  hello(room?: string): void {
    const code = room?.toUpperCase();
    if (!code) { this.send({ t: 'hello', v: PROTOCOL_VERSION, name: this.name, password: urlPassword() }); return; }
    this.roomCode = code;
    const own = readSlot();
    if (own && own.room === code) { // reload: this tab's own token, no need to ask other tabs
      this.send({ t: 'hello', v: PROTOCOL_VERSION, name: this.name, room: code, token: own.token, password: urlPassword() });
      return;
    }
    this.helloPending = true;
    void pickReconnectToken(code).then((token) => {
      this.helloPending = false;
      if (this.state === 'closed') return;
      this.transport.send(JSON.stringify({ t: 'hello', v: PROTOCOL_VERSION, name: this.name, room: code, token, password: urlPassword() } satisfies ClientControl));
      for (const m of this.preHello) this.transport.send(m);
      this.preHello.length = 0;
    });
  }
  create(map: MapName, settings: RoomSettings): void {
    this.send({ t: 'create', map, mode: 'sas', settings });
  }
  start(): void {
    this.send({ t: 'start' });
  }
  setReady(ready: boolean): void {
    this.send({ t: 'ready', ready });
  }
  sendLoaded(): void {
    this.send({ t: 'loaded' });
    this.state = 'playing';
  }
  close(): void {
    this.leaving = true;
    this.stopReconnect();
    this.stopWatchdog();
    writeSlot(null);
    clearSharedSlot(this.token);
    this.dropLock();
    this.send({ t: 'leave' });
    this.transport.close();
    this.state = 'closed';
  }

  /** Hold `bo2sns.slot.<token>` while this tab owns the slot (released when the tab closes). */
  private holdLock(token: string): void {
    if (this.lockedToken === token) return;
    this.dropLock();
    const locks = lockManager();
    if (!locks || !token) return;
    this.lockedToken = token;
    let release!: () => void;
    const held = new Promise<void>((r) => { release = r; });
    this.releaseLock = release;
    void locks.request(LOCK_PREFIX + token, {}, () => held).catch(() => { /* unsupported */ });
  }
  private dropLock(): void {
    this.releaseLock?.();
    this.releaseLock = null;
    this.lockedToken = '';
  }

  private stopWatchdog(): void {
    if (this.watchdogTimer) clearInterval(this.watchdogTimer);
    this.watchdogTimer = null;
  }

  /**
   * Reopen a socket that went silent (see header). A main-thread hitch (map load, tab in the
   * background) also delays our timer: if the watchdog itself ran late, the silence is not trusted.
   */
  private watchdog(): void {
    const now = performance.now();
    const late = this.lastWatchdog && now - this.lastWatchdog > WATCHDOG_MS * 3;
    this.lastWatchdog = now;
    if (late) { this.lastMsgAt = now; return; }
    if (this.state === 'closed' || this.state === 'connecting' || this.state === 'reconnecting' || this.leaving) return;
    if (!this.lastMsgAt) { this.lastMsgAt = now; return; }
    if (now - this.lastMsgAt < STALL_RECONNECT_MS) return;
    this.netDebug.watchdogReconnects++;
    this.lastMsgAt = now;
    this.onTransportClose(`no data from the server for ${STALL_RECONNECT_MS / 1000} s`, true);
  }

  // ------------------------------------------------------------------------------------------
  // Connection / reconnect
  // ------------------------------------------------------------------------------------------
  private onTransportClose(reason: string, force = false): void {
    this.world.connected = false;
    if (this.leaving || !isReopenable(this.transport) || !this.roomCode || !this.token || this.state === 'closed') {
      if (this.reconnectTimer && performance.now() < this.reconnectUntil) return; // a retry attempt failed: keep trying
      this.stopReconnect();
      this.stopWatchdog();
      if (this.state !== 'closed' || !this.lastError) this.lastError = reason; // keep e.g. the REPLACED explanation
      this.state = 'closed';
      return;
    }
    if (this.reconnectTimer) return;
    this.lastError = `connection lost (${reason}), reconnecting...`;
    this.state = 'reconnecting';
    this.reconnectUntil = performance.now() + RECONNECT_GRACE_MS;
    const t = this.transport as ReopenableTransport;
    const attempt = () => {
      if (performance.now() > this.reconnectUntil) {
        this.stopReconnect();
        this.state = 'closed';
        this.lastError = 'reconnect failed';
        return;
      }
      if (!t.isOpen) t.reopen();
    };
    this.reconnectTimer = setInterval(attempt, RECONNECT_RETRY_MS);
    if (force) t.reopen(); // the old socket still looks OPEN to the browser: replace it now
    else attempt();
  }

  private onTransportOpen(): void {
    if (this.state !== 'reconnecting') return;
    this.netDebug.reconnects++;
    this.send({ t: 'hello', v: PROTOCOL_VERSION, name: this.name, room: this.roomCode, token: this.token, password: urlPassword() });
  }

  private stopReconnect(): void {
    if (this.reconnectTimer) clearInterval(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  /** Forget all match-time net state (new `load`: new match or reconnect). */
  private resetNet(): void {
    this.clock = new ServerClock();
    this.snaps.clear();
    this.events.clear();
    this.predictor.reset();
    this.projectiles.clear();
    this.sentCmds.length = 0;
    this.world.players = [];
    this.world.missiles = [];
  }

  // ------------------------------------------------------------------------------------------
  // Messages
  // ------------------------------------------------------------------------------------------
  private onMessage(d: WireData): void {
    this.lastMsgAt = performance.now();
    if (typeof d !== 'string') {
      if (peekMsgType(d) !== MsgType.SNAP || !this.load) return;
      let s: Snapshot;
      try { s = decodeSnapshot(d, this.load.quant); } catch (e) {
        this.netDebug.decodeErrors++;
        console.warn('[net] bad snapshot', e);
        return;
      }
      this.onSnapshot(s, d.byteLength);
      return;
    }
    let m: ServerControl;
    try { m = JSON.parse(d) as ServerControl; } catch { return; }
    switch (m.t) {
      case 'welcome':
        this.world.connected = true;
        this.world.localClientNum = m.clientNum;
        this.room = m.room;
        this.token = m.token;
        this.roomCode = m.room.code;
        writeSlot({ room: m.room.code, token: m.token });
        this.holdLock(m.token);
        this.updatePlayerInfo(m.room);
        if (this.state === 'connecting' || this.state === 'reconnecting') {
          this.state = m.room.phase === 'lobby' ? 'lobby' : this.state === 'reconnecting' ? 'loading' : 'lobby';
        }
        if (this.reconnectTimer) { this.stopReconnect(); this.lastError = null; }
        break;
      case 'room':
        this.room = m.room;
        this.updatePlayerInfo(m.room);
        if (m.room.phase === 'lobby' && (this.state === 'ended' || this.state === 'playing' || this.state === 'loading')) {
          this.state = 'lobby';
          this.load = null;
        }
        break;
      case 'load':
        this.resetNet();
        this.load = { map: m.map, cmdTimeBase: m.cmdTimeBase, quant: { mapCenter: m.mapCenter, posScale: m.posScale } };
        this.world.map = m.map;
        this.world.timeLimitMs = (this.room?.settings.timeLimitMin ?? 0) * 60_000; // integration: HUD prematch timer
        this.cmdgen.reset(m.cmdTimeBase);
        this.state = 'loading';
        break;
      case 'ping':
        this.send({ t: 'pong', s: m.s });
        break;
      case 'scoreboard':
        this.world.scoreboard = m.rows;
        this.world.phase = m.phase;
        this.world.timeLeftMs = m.timeLeftMs;
        break;
      case 'end':
        this.state = 'ended';
        this.world.scoreboard = m.results;
        break;
      case 'error':
        this.lastError = `${m.code}: ${m.msg}`;
        if (this.state === 'reconnecting' && (m.code === 'ROOM_NOT_FOUND' || m.code === 'ROOM_FULL')) {
          this.stopReconnect();
          writeSlot(null);
          clearSharedSlot(this.token);
          this.state = 'closed';
        }
        if (m.code === 'REPLACED') {
          // another tab/socket reclaimed this slot with our token: stop here, never fight back
          this.leaving = true;
          this.stopReconnect();
          this.stopWatchdog();
          this.dropLock();
          writeStore('session', STORAGE_KEY, null);
          this.lastError = 'This player was taken over by another tab or connection.';
          this.world.connected = false;
          this.state = 'closed';
        }
        break;
      default:
        break;
    }
    for (const l of this.listeners) l(m);
  }

  private updatePlayerInfo(room: RoomState): void {
    this.world.playerInfo.clear();
    for (const p of room.players) this.world.playerInfo.set(p.clientNum, p);
  }

  private onSnapshot(s: Snapshot, bytes: number): void {
    const now = performance.now();
    this.lastSnapAt = now;
    this.netDebug.snapshots++;
    if (!this.snaps.push(s)) { this.netDebug.snapshotsDropped++; return; }
    this.clock.onSnapshot(s.serverTime, now);
    this.world.stats.snapBytes = bytes;
    this.world.stats.serverTick = s.tick;
    this.world.localClientNum = s.ps.clientNum;
    this.predictor.onSnapshot(s.ps, s.lastExecutedCmd, now, this.predEvents);
    this.events.pushSnapshot(s.serverTime, s.events);
    for (const e of s.events) if (e.type === GameEventType.MATCH) { this.world.phase = e.phase; this.world.timeLeftMs = e.timeLeftMs; }
  }

  // ------------------------------------------------------------------------------------------
  // Per render frame
  // ------------------------------------------------------------------------------------------
  /**
   * Sample input, interpolate remote entities at the clock's render time, generate due cmds (send
   * + predict against the interpolated players), deliver due events, build the local view.
   */
  frame(realNow: number, input: InputSource, ictx: InputContext, timeNudge: number): void {
    const w = this.world;
    const dtMs = this.lastFrameReal ? Math.min(250, realNow - this.lastFrameReal) : 0;
    this.lastFrameReal = realNow;
    w.frameEvents = [];
    const latest = this.snaps.latest;
    const p = this.predictor;
    // ---- input (look every frame) ------------------------------------------------------------
    const f = input.frame(ictx);
    this.lastInputFrame = f;
    if (!this.inputSuspended) this.cmdgen.addLook(f.yawDelta, f.pitchDelta, w.predicted.deltaAngles[0], PITCH_UP_LIMIT, PITCH_DOWN_LIMIT);
    // ---- clock + interpolation of remote players ----------------------------------------------
    if (latest && this.clock.ready) {
      w.renderTime = this.clock.renderTime(realNow, timeNudge, latest.serverTime);
      interpolatePlayers(this.snaps, w.renderTime, w.playerInfo, w.players);
      w.stats.renderLag = latest.serverTime - w.renderTime;
      w.stats.delta = this.clock.delta;
    }
    this.updateClipEntities();
    // ---- cmds: 60 Hz, send + predict --------------------------------------------------------
    if (this.state === 'playing' && latest && p.ready) {
      const n = this.cmdgen.due(realNow);
      for (let i = 0; i < n; i++) {
        const intent = this.inputSuspended ? NEUTRAL_INTENT : input.cmdIntent(ictx);
        const cmd = this.cmdgen.build(intent, w.renderTime, this.aimKickAngles);
        this.sendCmd(cmd, latest.tick);
        p.onCmd(cmd, this.predEvents);
      }
    }
    for (const e of this.predEvents) w.frameEvents.push({ src: 'pred', ent: w.localClientNum, ev: e.ev, parm: e.parm });
    this.predEvents.length = 0;
    // ---- server events due on the render timeline -------------------------------------------
    if (latest) this.events.drain(w.renderTime, w.frameEvents);
    // ---- local view (interpolate between predicted cmd states + error decay) ----------------
    w.predicted = p.predicted;
    // ---- stall: "Connection Interrupted" on the local HUD (Q3/IW CG_DrawDisconnect, see header) --
    const stalled = this.state === 'playing' && !!latest && this.lastSnapAt > 0 && realNow - this.lastSnapAt > CONNECTION_INTERRUPTED_MS;
    if (stalled && !this.interrupted) this.netDebug.stalls++;
    this.interrupted = stalled;
    if (stalled) {
      copyPlayerState(this.stalledPs, p.predicted);
      this.stalledPs.eFlags |= EF.CONNECTION_INTERRUPTED;
      w.predicted = this.stalledPs;
    }
    const alpha = this.cmdgen.frac(realNow);
    vlerp(w.view.origin, p.prev.origin, p.predicted.origin, alpha);
    const err = p.currentError(realNow, this.tmpErr);
    w.view.origin[0] += err[0]; w.view.origin[1] += err[1]; w.view.origin[2] += err[2];
    w.view.viewHeight = p.prev.viewHeightCurrent + (p.predicted.viewHeightCurrent - p.prev.viewHeightCurrent) * alpha;
    w.view.velocity = vclone(p.predicted.velocity);
    w.view.alpha = alpha;
    // render-rate view angles: raw client angles + server delta angles (Q3)
    for (let i = 0; i < 3; i++) w.view.viewangles[i] = this.cmdgen.viewangles[i] + shortToAngle(shortToSigned(p.predicted.deltaAngles[i]));
    // ---- missiles: interpolated server ones + own predicted ones ----------------------------
    if (latest && this.clock.ready) {
      const pose = this.parentPose;
      interpolateMissiles(this.snaps, w.renderTime, pose, w.missiles);
      this.projectiles.update(realNow, dtMs, latest.missiles, w.missiles, (m, o, a) => { resolveMissile(m, pose, o, a); }, pose);
    }
    // ---- stats ---------------------------------------------------------------------------
    this.updateStats(realNow);
  }

  /** Parent pose for stuck-to-player missiles: remote = interpolated, local = predicted view. */
  private readonly parentPose: ParentPose = (clientNum, out) => {
    const w = this.world;
    if (clientNum === w.localClientNum) {
      out[0] = w.view.origin[0]; out[1] = w.view.origin[1]; out[2] = w.view.origin[2];
      return w.view.viewangles[1];
    }
    const rp = w.getPlayer(clientNum);
    if (!rp) return null;
    out[0] = rp.origin[0]; out[1] = rp.origin[1]; out[2] = rp.origin[2];
    return rp.angles[1];
  };

  private updateClipEntities(): void {
    this.clipEnts.length = 0;
    this.targetPoses.length = 0;
    let k = 0;
    for (const rp of this.world.players) {
      if (!rp.alive || rp.moveFlags & MoveFlag.DEAD) continue;
      const e = this.clipPool[k] ?? (this.clipPool[k] = createClipEntity());
      const pose = this.posePool[k] ?? (this.posePool[k] = createPose());
      k++;
      this.clipEnts.push(setPlayerClipEntity(e, rp.clientNum, rp.origin, rp.stance));
      pose.clientNum = rp.clientNum; pose.alive = true; pose.spawnCount = rp.spawnCount;
      pose.origin[0] = rp.origin[0]; pose.origin[1] = rp.origin[1]; pose.origin[2] = rp.origin[2];
      pose.viewHeight = rp.viewHeight; pose.yaw = rp.angles[1];
      this.targetPoses.push(pose);
    }
  }

  private sendCmd(cmd: UserCmd, ackTick: number): void {
    // reliable transports: one cmd per packet; unreliable: resend the previous ones too (Q3 cl_packetdup)
    this.sentCmds.push(cmd);
    const keep = this.transport.reliable ? 1 : 1 + CMD_REDUNDANCY_UNRELIABLE;
    while (this.sentCmds.length > keep) this.sentCmds.shift();
    this.transport.send(encodeCmdPacket({ ackSnapTick: ackTick, cmds: this.sentCmds }));
    this.netDebug.cmdsSent++;
  }

  private updateStats(realNow: number): void {
    const w = this.world, p = this.predictor, d = this.netDebug;
    if (!this.statWindowStart) this.statWindowStart = realNow;
    if (realNow - this.statWindowStart >= 1000) {
      w.stats.correctionsPerSec = p.corrections - this.statCorrections;
      w.stats.extrapolationsPerSec = this.clock.extrapolations - this.statExtrap;
      w.stats.maxPredError = p.maxError;
      this.statCorrections = p.corrections;
      this.statExtrap = this.clock.extrapolations;
      p.maxError = 0;
      this.statWindowStart = realNow;
    }
    const me = w.scoreboard.find((x) => x.clientNum === w.localClientNum);
    if (me) w.stats.ping = me.ping;
    d.pendingCmds = p.pendingCount;
    d.corrections = p.corrections;
    d.lastPredError = p.lastError;
    d.lastCorrections = p.corrections_log;
    d.cmdSlotsDropped = this.cmdgen.dropped;
    d.clockResets = this.clock.resets;
    d.eventsDropped = this.events.dropped;
    d.snapshotsDropped = this.snaps.dropped;
    d.lastSnapshotDrop = this.snaps.lastDrop;
    d.projectilesLaunched = this.projectiles.launched;
    d.projectilesConfirmed = this.projectiles.confirmed;
    d.projectilesExpired = this.projectiles.expired;
  }
}
